/**
 * lazyWithRetry.ts
 *
 * Wrapper attorno a `React.lazy()` che gestisce gli errori di caricamento
 * dei chunk JavaScript dinamici. Questi errori si verificano tipicamente
 * dopo un nuovo deploy: il browser ha una vecchia versione dell'app in cache
 * e tenta di scaricare un chunk con un hash che non esiste più sul server.
 *
 * Strategia:
 * 1. Se l'import dinamico fallisce con un errore tipico di chunk obsoleto
 *    (es. "Failed to fetch dynamically imported module", "Loading chunk failed",
 *    "Importing a module script failed"), ricarichiamo automaticamente la pagina
 *    UNA SOLA VOLTA (flag in sessionStorage) per scaricare la versione nuova.
 * 2. Se anche dopo il reload l'errore persiste, lasciamo propagare per non
 *    intrappolare l'utente in un loop di reload infinito; l'ErrorBoundary
 *    globale mostrerà la UI di recovery.
 */

import { lazy, ComponentType } from "react";

const RELOAD_FLAG = "lovable:chunk-reload-attempt";

// Pattern degli errori che indicano un chunk obsoleto / non più disponibile.
const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\d]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
];

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Caricamento riuscito: resettiamo il flag così future occorrenze
      // potranno di nuovo provare il reload automatico.
      try {
        window.sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* sessionStorage non disponibile: ignoriamo */
      }
      return mod;
    } catch (error) {
      if (isChunkLoadError(error)) {
        let alreadyReloaded = false;
        try {
          alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === "1";
        } catch {
          /* ignore */
        }
        if (!alreadyReloaded) {
          try {
            window.sessionStorage.setItem(RELOAD_FLAG, "1");
          } catch {
            /* ignore */
          }
          console.warn(
            "[lazyWithRetry] Chunk obsoleto rilevato, ricarico la pagina per scaricare la versione aggiornata.",
            error,
          );
          window.location.reload();
          // Restituiamo una Promise mai risolta: il reload sostituirà la pagina.
          return new Promise<{ default: T }>(() => {});
        }
        console.error(
          "[lazyWithRetry] Chunk ancora non caricabile dopo reload automatico.",
          error,
        );
      }
      throw error;
    }
  });
}